import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  ModifiedQuestion,
  QuestionType,
  QuizType,
} from '@/client/quizzes/types';
import {
  ModifiedQuiz,
  quizSupportedLanguages,
} from '@/client/quizzes/types/quizzes.type';
import { useQuiz } from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { LanguageTabs } from '@/ui/language-tabs';
import { FlexContainer } from '@/ui/styled-ui';
import {
  convertQuestionsToArrayFormat,
  getFullyTranslatedLanguages,
  GlobeIcon,
  modifyCourseQuiz,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FieldArray, Form, Formik, useFormikContext } from 'formik';
import { Dialog, DialogProps } from 'primereact/dialog';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SurveyQuestionEdit } from './SurveyQuestionEdit';
import { surveySchema } from './validations/survey.validation';

const StyledDialog = styled(Dialog)`
  width: 649px;
  height: 646px;
`;

const StyledAddButton = styled(FlexContainer)`
  padding: var(--default-padding);
  align-items: center;
  flex-shrink: 0;
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--beige-main);
  color: var(--gray-darker);
  font-weight: 500;
  margin-top: var(--medium-padding);
  cursor: pointer;
  transition: background 0.3s ease;
  i {
    color: var(--black-main);
  }
`;

type SurveyModalProps = {
  refetch: () => void;
  surveyId?: string;
} & DialogProps;

type SurveyFormFieldsProps = {
  validOptions: string[];
  setValidOptions: (options: string[]) => void;
  selectedTab: LanguagesEnum;
  setSelectedTab: (tab: LanguagesEnum) => void;
  requiredTranslations: string[];
  isLoading: boolean;
  isSubmitting: boolean;
  survey: { supportedLanguages?: LanguagesEnum[] } | undefined;
  onHide: () => void;
};

const SurveyFormFields: React.FC<SurveyFormFieldsProps> = ({
  validOptions,
  setValidOptions,
  selectedTab,
  setSelectedTab,
  requiredTranslations,
  isLoading,
  isSubmitting,
  survey,
  onHide,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<ModifiedQuiz>();

  useEffect(() => {
    setValidOptions(
      getFullyTranslatedLanguages(quizSupportedLanguages, values.questions),
    );
  }, [values.questions]);

  return (
    <Form>
      <FlexContainer justify="flex-start" direction="column" gap={16}>
        <FlexContainer justify="flex-start" gap={8}>
          <GlobeIcon />
          {t('content.chosen.languages')}
        </FlexContainer>
        <LanguageTabs
          defaultSelected={selectedTab}
          options={quizSupportedLanguages}
          requiredOptions={requiredTranslations}
          validOptions={validOptions}
          isDisabled={isLoading || isSubmitting}
          onToggle={(option) => {
            const newTab = LanguagesEnum[option as keyof typeof LanguagesEnum];
            setSelectedTab(newTab);

            if (!survey?.supportedLanguages?.includes(newTab)) {
              setFieldValue(
                'questions',
                values.questions.map((question) => ({
                  ...question,
                  translations: {
                    [newTab]: { text: '' },
                    ...question.translations,
                  },
                })),
              );
            }
          }}
        />
      </FlexContainer>

      <FieldArray name="questions">
        {({ push, remove }) => (
          <>
            {values.questions.map(
              (question: ModifiedQuestion, index: number) => (
                <SurveyQuestionEdit
                  key={index}
                  questionIndex={index}
                  question={question}
                  selectedTab={selectedTab}
                  onRemove={() => remove(index)}
                  hasValidOption={!!validOptions.length}
                  hasFeedback={
                    !!values.questions.some(
                      (q: ModifiedQuestion) => q.type === QuestionType.TEXT,
                    )
                  }
                />
              ),
            )}
            <StyledAddButton
              id="add-question-button"
              justify="center"
              align="center"
              onClick={() => {
                push({
                  position: values.questions.length + 1,
                  translations: {},
                  answerOptions: [],
                  scaleMin: 0,
                  scaleMax: 1,
                  type: QuestionType.RATING_SCALE,
                } as ModifiedQuestion);
              }}
            >
              <i className="pi pi-plus" />
              <span className="ml-2">{t('content.add.question')}</span>
            </StyledAddButton>
          </>
        )}
      </FieldArray>

      <FlexContainer justify="flex-start" className="mt-5" gap={16}>
        <AppButton
          label={t('security.culture.save.survey')}
          isDisabled={isSubmitting || isLoading || !!Object.keys(errors).length}
          isSubmit
        />
        <AppButton
          label={t('button.cancel')}
          severity="secondary"
          type="outlined"
          onClick={onHide}
          className="mr-3"
        />
      </FlexContainer>
    </Form>
  );
};

export const SurveyModal: React.FC<SurveyModalProps> = ({
  onHide,
  visible,
  surveyId,
  refetch,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [selectedTab, setSelectedTab] = useState<LanguagesEnum>(
    LanguagesEnum.EN,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validOptions, setValidOptions] = useState<string[]>([]);

  const [requiredTranslations, setRequiredTranslations] = useState([
    languageDisplayCode[LanguagesEnum.EN],
  ]);

  const [initialValues, setInitialValues] = useState<ModifiedQuiz>({
    title: t('security.culture.survey.full'),
    questions: [],
    account:
      currentAccount?.isSystem || !currentAccount?.id
        ? null
        : currentAccount?.id,
    type: QuizType.SURVEY,
  });

  const { quiz: survey, isLoading } = useQuiz(currentAccount?.id as string, {
    quizId: surveyId,
    enabled: !!surveyId,
  });

  useEffect(() => {
    if (!surveyId || !survey || isLoading) return;
    setSelectedTab(
      survey?.supportedLanguages?.length
        ? (survey?.supportedLanguages[0] as LanguagesEnum)
        : LanguagesEnum.EN,
    );

    setInitialValues({
      title: survey?.title || t('security.culture.survey.full'),
      questions: modifyCourseQuiz(survey)?.questions || [],
      account:
        currentAccount?.isSystem || !currentAccount?.id
          ? null
          : currentAccount?.id,
      type: QuizType.SURVEY,
    });
  }, [surveyId, isLoading, survey]);

  const handleSaveSurvey = async (values: ModifiedQuiz) => {
    setIsSubmitting(true);

    try {
      const surveyData = {
        title: values.title,
        questions: convertQuestionsToArrayFormat(values.questions, true),
        account:
          currentAccount?.isSystem || !currentAccount?.id
            ? null
            : currentAccount?.id,
        type: QuizType.SURVEY,
      };

      if (surveyId && survey) {
        await client.quizzes.updateQuiz(surveyId, surveyData);
      } else {
        await client.quizzes.createQuiz(surveyData);
      }

      onHide();
      refetch();
      toast?.success(
        t('toast.success'),
        surveyId && survey ? t('sc.survey.edited') : t('sc.survey.created'),
      );
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSurveyModalHide = () => {
    onHide();
    setRequiredTranslations([languageDisplayCode[LanguagesEnum.EN]]);
  };

  return (
    <StyledDialog
      id="survey-modal"
      blockScroll
      visible={visible}
      header={<h1>{survey?.title || t('security.culture.survey.full')}</h1>}
      onHide={onSurveyModalHide}
      draggable={false}
      className="v2"
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={surveySchema(t, !!validOptions.length, selectedTab)}
        validateOnMount
        onSubmit={handleSaveSurvey}
      >
        <SurveyFormFields
          validOptions={validOptions}
          setValidOptions={setValidOptions}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          requiredTranslations={requiredTranslations}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          survey={survey}
          onHide={onHide}
        />
      </Formik>
    </StyledDialog>
  );
};
